import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Phone, CalendarDays, Plus, Pencil, Mail, MapPin, Cake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAppState } from "@/context/AppState";
import { formatPrecio, mergeClients, formatBirthday } from "@/lib/agenda";

const emptyForm = { name: "", phone: "", email: "", address: "", birthday: "" };

export default function Clientes() {
  const { appointments, clients, addClient, updateClient } = useAppState();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const merged = useMemo(
    () => mergeClients(clients, appointments),
    [clients, appointments]
  );

  const filtered = merged.filter(
    (c) =>
      c.name.toLowerCase().includes(query.toLowerCase()) ||
      (c.phone || "").includes(query)
  );

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.managed ? c.id : null);
    setForm({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      address: c.address || "",
      birthday: c.birthday || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Pon al menos el nombre.");
      return;
    }
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      birthday: form.birthday || null,
    };
    try {
      if (editingId) {
        await updateClient(editingId, payload);
        toast.success("Ficha actualizada.");
      } else {
        await addClient(payload);
        toast.success("Ficha guardada.");
      }
      setOpen(false);
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="clientes-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Tus clientes</h1>
          <p className="text-sm text-slate-500 mt-1">Completa la ficha con sus datos y su cumpleaños.</p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-full text-white gap-2 shrink-0"
          style={{ backgroundColor: "#275FBE" }}
          data-testid="add-client-button"
        >
          <Plus className="h-4 w-4" />
          Añadir cliente
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar cliente"
          className="pl-9 rounded-full"
          data-testid="client-search"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-400">Todavía no tienes clientes aquí. Añade el primero cuando quieras.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="clients-list">
          {filtered.map((c, i) => (
            <div
              key={c.phone || c.name || i}
              className="bg-white rounded-2xl border border-[#EAE5DF] p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)] hover:shadow-[0_14px_32px_-12px_rgba(46,42,143,0.18)] hover:-translate-y-0.5 transition-all duration-300"
              data-testid={`client-card-${c.phone || c.name}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold shrink-0">
                    {c.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {c.phone || "Sin teléfono"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => openEdit(c)}
                  className="text-slate-400 hover:text-[#275FBE] transition-colors p-1"
                  title="Editar ficha"
                  data-testid={`edit-client-${c.phone || c.name}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3 space-y-1 text-xs text-slate-500">
                {c.email && (
                  <p className="inline-flex items-center gap-1.5 truncate">
                    <Mail className="h-3 w-3" /> {c.email}
                  </p>
                )}
                {c.address && (
                  <p className="inline-flex items-center gap-1.5 truncate">
                    <MapPin className="h-3 w-3" /> {c.address}
                  </p>
                )}
                {c.birthday && (
                  <p className="inline-flex items-center gap-1.5 text-[#2E2A8F]">
                    <Cake className="h-3 w-3" /> {formatBirthday(c.birthday)}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-sm">
                <span className="text-slate-500 inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" /> {c.count} citas
                </span>
                {c.pending > 0 ? (
                  <span className="text-amber-700 font-medium">
                    {formatPrecio(c.pending)} pendiente
                  </span>
                ) : (
                  <span className="text-emerald-700 font-medium">Al día</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px] rounded-3xl" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">
              {editingId ? "Editar ficha" : "Añadir cliente"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Guarda sus datos de contacto y su cumpleaños.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Nombre</Label>
              <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-address">Dirección</Label>
              <Input id="c-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-phone">Teléfono</Label>
                <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-birthday">Fecha de cumpleaños</Label>
              <Input id="c-birthday" type="date" value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="client-birthday" />
            </div>
          </div>
          <Button
            className="w-full rounded-full text-white"
            style={{ backgroundColor: "#275FBE" }}
            onClick={save}
            data-testid="save-client-button"
          >
            Guardar ficha
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
